import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Title from "../../components/common/Title";
import SubTitle from "../../components/common/SubTitle";
import { CampaignButtonVariant } from "../../components/common/Button/Button";
import BlogCard from "../../components/common/BlogCard/BlogCard";
import { fetchBlogsAction } from "../../store/slices/blogSlice";
// Decorative assets
import { Images } from "../../utils/Images";

const RecentBlogs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  // Fixed: use proper breakpoint syntax for hiding below 900px
  const isBelow900 = useMediaQuery("(max-width: 899px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list } = useSelector((state) => state.blog);

  React.useEffect(() => {
    if (!list || list.length === 0) {
      dispatch(fetchBlogsAction());
    }
  }, [dispatch, list]);

  const recentBlogs = React.useMemo(() => {
    if (!Array.isArray(list) || list.length === 0) return [];
    // Build card data from latest 3 blogs by createdAt
    const sorted = [...list].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    });
    const topThree = sorted.slice(0, 3);
    const toText = (html) => {
      if (!html) return "";
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || "").trim();
    };
    return topThree.map((b) => ({
      id: b.id,
      image: b.banner_image,
      title: b.title,
      description: toText(b?.BlogDescs?.[0]?.description || "").slice(0, 160),
      date: b.createdAt,
    }));
  }, [list]);

  // Hide the entire component on screens 899px and below

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        maxWidth: isMobile ? "100%" : "1440px",
        borderRadius: isMobile ? "16px" : "36px",
        backgroundColor: "#E8E8E8",
        margin: "40px auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        alignItems: "center",
        padding: isMobile ? "26px 10px 20px 10px" : "60px 10px 40px 10px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Elements */}
      <img
        src={Images.orangeSparks}
        alt="Orange Sparks"
        style={{
          position: "absolute",
          top: isMobile ? "10px" : isAbove1200 ? "61px" : "20px",
          left: isMobile ? "10px" : isAbove1200 ? "116px" : "20px",
          width: isMobile ? "40px" : isAbove1200 ? "50px" : "45px",
          display: isMobile ? "none" : "block",
        }}
      />
      <img
        src={Images.heart}
        alt="Images.heart"
        style={{
          position: "absolute",
          top: isMobile ? "15px" : isAbove1200 ? "116px" : "80px",
          right: isMobile ? "15px" : isAbove1200 ? "80px" : "80px",
          width: isMobile ? "30px" : isAbove1200 ? "22px" : "25px",
          display: isMobile ? "none" : "block",
        }}
      />
      {/* <img
        src={Images.stringHand}
        alt="String Hand"
        style={{
          position: "absolute",
          bottom: "16px",
          left: "0px",
          width: "53px",
          display: isMobile ? "none" : "block",
          transform: "scaleX(-1)",
        }}
      /> */}

      {/* Title */}
      <Box
        sx={{ mb: isMobile ? "8px" : "16px", zIndex: 2, position: "relative" }}
      >
        <SubTitle isMobile={isMobile}>Recent Blogs</SubTitle>
        <img
          src={Images.redHearts}
          alt="RedImages.heart"
          style={{
            position: "absolute",
            top: isMobile ? "0" : isAbove1200 ? "20%" : "20%",
            right: isMobile ? "-50px" : isAbove1200 ? "-50px" : "-50px",
            width: isMobile ? "30px" : isAbove1200 ? "30px" : "30px",
            height: isMobile ? "30px" : isAbove1200 ? "30px" : "30px",
            display:"block",
          }}
        />
      </Box>

      {/* Subtitle */}
      <Box
        sx={{
          width: "100%",
          maxWidth: isMobile ? "280px" : "581px",
          mb: isMobile ? "20px" : "38px",
          fontSize: "14px",
        }}
      >
        <Typography
          isMobile={isMobile}
          sx={{
            fontSize: isMobile ? "12px" : "14px",
            color: "#666666",
            textAlign: "center",
            fontWeight: 400,
            paddingInline: "10px",
          }}
        >
          Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
          eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad
          minim venia
        </Typography>
      </Box>

      {/* Blog Cards */}
      <Box
        sx={{
          width: "100%",
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "row" : "initial",
          overflowX: isMobile ? "auto" : "visible",
          overflowY: "hidden",
          scrollSnapType: isMobile ? "x mandatory" : "none",
          gap: { xs: "16px", md: "24px" },
          mb: { xs: "20px", md: "48px" },
          scrollPaddingLeft: isMobile ? "10px" : "0px",
          scrollPaddingRight: isMobile ? "10px" : "0px",
          paddingLeft: isMobile ? "10px" : "40px",
          paddingRight: isMobile ? "10px" : "40px",
          gridTemplateColumns: isMobile
            ? "none"
            : "repeat(auto-fit, minmax(280px, 1fr))",
          justifyItems: "center",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {recentBlogs.map((blog) => (
          <Box
            key={blog.id}
            sx={{
              width: "100%",
              maxWidth: isMobile ? "280px" : "100%",
              flex: isMobile ? "0 0 auto" : "initial",
            }}
          >
            <BlogCard blogData={blog} />
          </Box>
        ))}
      </Box>

      {/* View All Button */}
      <Box>
        <CampaignButtonVariant
          text="View All Blogs"
          isMobile={isMobile}
          onClick={() => navigate("/blogs")}
        />
      </Box>
    </Box>
  );
};

export default RecentBlogs;
